import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import TrainingSummaryService from "../../../services/trainingSummaryService";
import {
  loadSlideNotes,
  slideNotesInOrder,
  buildSummaryCopyText,
} from "../../add_sound_modal/slideNotes";

const DocumentSummaryPanel = ({ baseUrl, trainingId, navItems, navItemsIds }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [notesByNavId, setNotesByNavId] = useState(null);

  useEffect(() => {
    const fetchSummary = async () => {
      if (!trainingId) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await TrainingSummaryService.getTrainingSummary({ trainingId });
        setSummaryData(data);
      } catch (err) {
        console.error("Error fetching training summary:", err);
        setError(err.message || i18n.t("AudioPanel.ErrorLoadingData"));
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [trainingId]);

  useEffect(() => {
    let alive = true;
    loadSlideNotes(trainingId).then((m) => {
      if (alive) setNotesByNavId(m);
    });
    return () => {
      alive = false;
    };
  }, [trainingId]);

  const noteEntries = slideNotesInOrder(notesByNavId, navItemsIds, navItems);
  const copyText = buildSummaryCopyText(
    summaryData?.courseSummary,
    noteEntries,
    i18n.t("AudioPanel.SlideNotes")
  );

  const copyToClipboard = async () => {
    if (!copyText) return;

    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: "12px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <div
          style={{
            fontSize: "14px",
            fontWeight: 600,
            color: "#0f172a",
          }}
        >
          {i18n.t("AudioPanel.DocumentSummary")}
        </div>
        {copyText && (
          <button
            type="button"
            onClick={copyToClipboard}
            style={{
              fontSize: 11,
              padding: "4px 8px",
              borderRadius: 6,
              border: "1px solid #E5E7EB",
              backgroundColor: copied ? "#10B981" : "#F9FAFB",
              color: copied ? "#FFFFFF" : "#6B7280",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            title={i18n.t("AudioPanel.CopyAll")}
          >
            {copied ? i18n.t("AudioPanel.Copied") : i18n.t("AudioPanel.CopyAll")}
          </button>
        )}
      </div>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        {loading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#8F8F8F",
              fontSize: "13px",
              padding: "20px",
            }}
          >
            {i18n.t("AudioPanel.Loading")}
          </div>
        )}
        {error && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#EF4444",
              fontSize: "13px",
              padding: "20px",
              textAlign: "center",
            }}
          >
            {i18n.t("AudioPanel.ErrorLoadingData")}
          </div>
        )}
        {(copyText || (!loading && !error)) && (
          <>
            {copyText ? (
              <div
                style={{
                  padding: "8px",
                }}
              >
                {!loading && !error && summaryData?.courseSummary && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#374151",
                      lineHeight: "1.6",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {summaryData.courseSummary}
                  </div>
                )}
                {noteEntries.length > 0 && (
                  <div
                    style={{ marginTop: !loading && !error && summaryData?.courseSummary ? 16 : 0 }}
                  >
                    <div
                      style={{ fontSize: 12, fontWeight: 600, color: "#0f172a", marginBottom: 8 }}
                    >
                      {i18n.t("AudioPanel.SlideNotes")}
                    </div>
                    {noteEntries.map((e) => (
                      <div key={e.navId} style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>
                          {e.title}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#374151",
                            lineHeight: "1.6",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {e.text}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#8F8F8F",
                  fontSize: "13px",
                  padding: "20px",
                  textAlign: "center",
                }}
              >
                {i18n.t("AudioPanel.NoSummaryAvailable")}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

DocumentSummaryPanel.propTypes = {
  baseUrl: PropTypes.string,
  trainingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  navItems: PropTypes.object,
  navItemsIds: PropTypes.array,
};

export default DocumentSummaryPanel;
